/**
 * Durable sign-in / API trace in sessionStorage (survives console clears).
 * Read with getAuthTraceText(), copy to bug reports, or use "Copy error details" on the sign-in error screen in the UI.
 */
const STORAGE_KEY = "integrator:auth-trace";
const MAX = 300;

type TraceEntry = { t: string; phase: string; data: Record<string, unknown> };

function readAll(): TraceEntry[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as TraceEntry[]) : [];
  } catch {
    return [];
  }
}

function writeAll(entries: TraceEntry[]): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Storage full or disabled — ignore
  }
}

export function appendAuthTrace(phase: string, data: Record<string, unknown> = {}): void {
  if (typeof window === "undefined") return;
  const next: TraceEntry = {
    t: new Date().toISOString(),
    phase,
    data: { ...data },
  };
  const list = readAll();
  list.push(next);
  while (list.length > MAX) {
    list.shift();
  }
  writeAll(list);
}

export function getAuthTraceText(): string {
  const list = readAll();
  if (list.length === 0) {
    return "(no trace yet — run through sign-in again)\n";
  }
  return list.map((e) => `${e.t}\t${e.phase}\t${JSON.stringify(e.data)}`).join("\n");
}

export function getAuthTraceJson(): TraceEntry[] {
  return readAll();
}

export function clearAuthTrace(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
